import { CellStyle, ColGroupDef, DefaultMenuItem, GetMainMenuItemsParams, MenuItemDef } from 'ag-grid-enterprise'
import { VariableType } from '../../../../../../components/Table/types'
import { CustomReportCategory } from '../../../../../../redux/context/customReports/typesCategory'
import { CustomReportVariable } from '../../../../../../redux/context/customReports/typesVariable'
import { getCellBackgroundColor, renderVariableTitle } from './renderingUtils'
import { getCategoryVariableType } from '../../utils'
import { CreateInnerHeaderComponent } from '../HeaderComponent'
import CustomGroupCellRenderer from '../CustomGroupCellRenderer' // Import new renderer
import { getFormattedValue } from './valueFormatting'
import CustomCellRenderer from '../CustomCellRenderer'
import { TypedColDef } from '../types'
import { Dimension } from '../../../../../../features/dimension/types/Dimension'
import { Company } from '../../../../../../types/company/Company'
import { getCategoryOwnTitle } from '../../print/CategoryTitleVisitor'

const AUTO_GROUP_MIN_WIDTH = 150
const CALCULATED_MAX_WIDTH = 400
const EXPAND_BUTTON_WIDTH = 20
const CELL_PADDING = 16
const CHAR_WIDTH = 6.1
const INDENT_WIDTH = 8

export const calculateAutoGroupWidth = (variables: CustomReportVariable[]): number => {
  if (!variables?.length) return AUTO_GROUP_MIN_WIDTH

  let maxWidth = 0

  const measure = (vars: CustomReportVariable[], depth: number) => {
    for (const v of vars) {
      const title = renderVariableTitle(v, []) || ''
      const indent = ((v.style?.indentLevel || 0) + depth) * INDENT_WIDTH
      const textWidth = title.length * CHAR_WIDTH
      maxWidth = Math.max(maxWidth, EXPAND_BUTTON_WIDTH + CELL_PADDING + indent + textWidth)
      if (v.children?.length) {
        measure(v.children, depth + 1)
      }
    }
  }
  measure(variables, 0)
  return Math.min(Math.max(Math.ceil(maxWidth), AUTO_GROUP_MIN_WIDTH), CALCULATED_MAX_WIDTH)
}

export const calculateCategoryNameWidth = (names: string[]): number => {
  if (!names?.length) return AUTO_GROUP_MIN_WIDTH

  let maxWidth = 0
  for (const name of names) {
    maxWidth = Math.max(maxWidth, CELL_PADDING + name.length * CHAR_WIDTH)
  }

  return Math.min(Math.max(Math.ceil(maxWidth), AUTO_GROUP_MIN_WIDTH), CALCULATED_MAX_WIDTH)
}

export const createAutoGroupColumnDef = (): TypedColDef => ({
  field: 'name',
  headerName: '',
  pinned: 'left',
  cellClassRules: {
    bold: params => !!params.data?.style?.fontWeight,
    italic: params => params.data?.style?.fontStyle === 'italic'
  },
  valueGetter: params => renderVariableTitle(params.data as CustomReportVariable, []),
  cellStyle: params => {
    const variable = params?.data as CustomReportVariable
    const style: CellStyle = {
      backgroundColor: getCellBackgroundColor(undefined, variable)
    }
    return style
  },
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  suppressHeaderContextMenu: true,
  cellRenderer: CustomGroupCellRenderer,
  suppressSizeToFit: false,
  resizable: true
})

/**
 * Counts all nodes in the tree (including the root node)
 */

export const createColumnDefinitions = (categories: CustomReportCategory[]): (TypedColDef | ColGroupDef)[] => {
  const createColumnDefs = (
    category: CustomReportCategory,
    inheritedCompany?: Company | null,
    inheritedDimension?: Dimension | null
  ): TypedColDef | ColGroupDef => {
    const isFunction = category.type === 'function'
    const columnVariableType = isFunction ? getCategoryVariableType(category) : undefined
    const headerName = getCategoryOwnTitle(category)

    const currentDimension = category.dimension || inheritedDimension
    const currentCompany = category.company || inheritedCompany
    const config = {
      field: category.id,
      mainMenuItems: (params: GetMainMenuItemsParams) => {
        const menuItems: (MenuItemDef | DefaultMenuItem)[] = []
        const itemsToExclude = ['expandAll', 'columnChooser'] as DefaultMenuItem[]
        params.defaultItems.forEach(item => {
          if (itemsToExclude.indexOf(item) < 0) {
            menuItems.push(item)
          }
        })
        return menuItems
      },
      minWidth: columnVariableType === VariableType.percentage ? 90 : 110,
      flex: 1,
      wrapHeaderText: true,
      sortable: true,
      type: columnVariableType,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      editable: false,
      headerName,
      cellRenderer: CustomCellRenderer,
      headerComponentParams: {
        innerHeaderComponent: CreateInnerHeaderComponent,
        template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label-category" role="presentation">
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
          <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
        </div>
      </div>`
      },
      headerStyle: {
        textAlign: 'left',
        fontSize: 10,
        lineHeight: 1,
        whiteSpace: 'pre-line'
      },
      // Pass category info through context for the renderer
      context: {
        category,
        company: currentCompany,
        dimension: currentDimension
      },
      cellClassRules: {
        bold: params => !!(params.data as CustomReportVariable)?.style?.fontWeight,
        italic: params => (params.data as CustomReportVariable)?.style?.fontStyle === 'italic'
      },

      cellStyle: params => {
        const variable = params?.data as CustomReportVariable

        const style: CellStyle = {
          backgroundColor: getCellBackgroundColor(category, variable, params.value),
          textAlign: 'right'
        }

        return style
      },

      // Keep valueFormatter for export functionality
      valueFormatter: params => getFormattedValue(params.value, params.data, category),
      valueGetter: params => params.data?.balances?.find(b => b.groupId === category.id)?.value
    } as TypedColDef

    if (!isFunction && category?.children) {
      return {
        ...config,
        children: category.children.map((child: CustomReportCategory) =>
          createColumnDefs(child, currentCompany, currentDimension)
        )
      }
    }
    return config
  }

  return categories.map(cat => createColumnDefs(cat, null, null))
}
